/**
 * Ein Treffer aus einer freien Datenbank: Bild, Name, Marke, Inhalt und
 * Nährwerte, dazu Quelle und Lizenz. Darunter die Wahl, ob ein neues Produkt
 * entsteht oder der Treffer als weitere Packung an ein vorhandenes Produkt
 * kommt.
 */
import { SHOPPING_CATEGORIES, formatDecimal, shoppingKey } from '@cookbook/domain';
import { useMemo, useState } from 'react';
import { ApiError, api } from '../../api/client.ts';
import type { FoundProduct, Product } from '../../api/types.ts';
import { useCachedQuery } from '../../api/useCachedQuery.ts';
import { texts } from '../../texts/de.ts';
import { FoodDetails, SourceNote } from './SourceNote.tsx';

interface FoundProductCardProps {
  readonly found: FoundProduct;
  /** Text des Knopfs, zum Beispiel „Übernehmen und auf die Liste“. */
  readonly actionLabel: string;
  readonly onAdopted: (product: Product, warnings: readonly string[]) => void;
}

/** Eingabefeld im einheitlichen Stil. */
const inputClass = 'w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-sm';

/** Treffer mit Übernahme. */
export function FoundProductCard({ found, actionLabel, onAdopted }: FoundProductCardProps) {
  const products = useCachedQuery<{ products: Product[] }>('/api/products', 'einkauf.produkte');
  const all = useMemo(() => products.data?.products ?? [], [products.data]);
  // Passt der Name zu einem vorhandenen Produkt, wird es vorgeschlagen.
  const match = useMemo(() => {
    const key = shoppingKey(found.name);
    return all.find((product) => key.includes(shoppingKey(product.name))) ?? null;
  }, [all, found.name]);
  const [mode, setMode] = useState<'new' | 'existing' | null>(null);
  const effectiveMode = mode ?? (match === null ? 'new' : 'existing');
  const [name, setName] = useState(found.name);
  const [category, setCategory] = useState(found.category);
  const [productId, setProductId] = useState<string>('');
  const selectedId = productId === '' ? (match?.id ?? '') : productId;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const size =
    found.packageAmount === null
      ? (found.quantityText ?? '')
      : `${formatDecimal(found.packageAmount)} ${found.packageUnit}`;

  async function adopt() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<{ product: Product; warnings: string[] }>('/api/products/from-source', {
        source: found.source,
        sourceId: found.sourceId,
        ...(effectiveMode === 'existing' ? { productId: selectedId } : { name, category }),
      });
      onAdopted(result.product, result.warnings);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-2xl border border-border-subtle bg-surface p-3">
      <div className="flex gap-3">
        {found.imageUrl === null ? null : (
          // Vorschau direkt aus der Quelle; gespeichert wird das Bild erst beim Übernehmen.
          <img
            src={found.imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="h-20 w-20 shrink-0 rounded-xl bg-white object-contain"
          />
        )}
        <div className="min-w-0 text-sm">
          <p className="font-semibold">{found.name}</p>
          <p className="text-content-muted">{[found.brand, size].filter((part) => part).join(' · ')}</p>
          {found.nutrition === null ? null : (
            <p className="text-content-muted">
              {formatDecimal(found.nutrition.kcal, 0)} kcal {texts.scan.per100}
            </p>
          )}
          <p className="text-xs text-content-muted">EAN {found.ean}</p>
        </div>
      </div>
      <FoodDetails allergens={found.allergens} labels={found.labels} />

      <div className="space-y-2 text-sm" role="radiogroup" aria-label={texts.scan.adoptAs}>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={effectiveMode === 'new'}
            onChange={() => setMode('new')}
            className="accent-accent"
          />
          {texts.scan.asNew}
        </label>
        {effectiveMode === 'new' ? (
          <div className="grid gap-2 pl-6 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-content-muted">{texts.scan.productName}</span>
              <input value={name} onChange={(event) => setName(event.target.value)} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-content-muted">{texts.products.category}</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className={inputClass}
              >
                {SHOPPING_CATEGORIES.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-content-muted sm:col-span-2">{texts.scan.nameHint}</p>
          </div>
        ) : null}
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={effectiveMode === 'existing'}
            onChange={() => setMode('existing')}
            disabled={all.length === 0}
            className="accent-accent"
          />
          {texts.scan.asVariant}
        </label>
        {effectiveMode === 'existing' ? (
          <div className="pl-6">
            <select
              value={selectedId}
              onChange={(event) => setProductId(event.target.value)}
              aria-label={texts.scan.asVariant}
              className={inputClass}
            >
              <option value="">{texts.scan.chooseProduct}</option>
              {all.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <SourceNote found={found} />
      {message === null ? null : <p className="text-sm text-accent-strong">{message}</p>}
      <button
        type="button"
        onClick={() => void adopt()}
        disabled={busy || (effectiveMode === 'new' ? name.trim() === '' : selectedId === '')}
        className="w-full rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent disabled:opacity-50"
      >
        {busy ? texts.scan.adopting : actionLabel}
      </button>
    </div>
  );
}
